import { Auth } from 'aws-amplify';
import React, { FC, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, notification, Button } from 'src/common';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'src/pages/paths';
import styles from '../index.module.scss';
import { ONLY_DIGITS_REGEX } from 'src/helpers/constants';
import { usePasswordValidator } from 'src/helpers/passwords';
import AuthBack from '../AuthBack';
import { InfoLabel } from 'src/shared';
import { AuthUser, useApp, UserType } from 'src/models/auth';
import { StorageKey } from 'src/types/enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';

interface FormState {
  code: string;
  username: string;
  password: string;
}

export const ResetPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { email, permission, handleFreeUser, handlePaidUser } = useApp();

  const navigate = useNavigate();
  const from = sessionStorage.getItem(StorageKey.FromLogin);
  const redirectTo = useMemo(
    () => (!!from ? from : permission === UserType.Free ? Paths.FREE_HOME_PATH : Paths.HOME_PATH),
    [from, permission]
  );

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const resendCode = useCallback(() => {
    email &&
      Auth.forgotPassword(email)
        .then(() => {
          notification.success({
            message: t('Auth.checkEmailForCode'),
            description: t('Auth.checkEmailForCode')
          });
        })
        .catch(() => {
          notification.error({
            message: t('Auth.codeNotSent'),
            description: t('Common.unknownErrorDesc')
          });
        });
  }, [email, t]);

  const initialValues = useMemo((): FormState => {
    return {
      code: '',
      username: email ?? '',
      password: ''
    };
  }, [email]);

  const onFinish = useCallback(
    (values: FormState) => {
      const { code, password } = values;
      setIsLoading(true);
      email &&
        Auth.forgotPasswordSubmit(email, code, password)
          .then(() => {
            setIsLoading(false);
            Auth.signIn(email, password)
              .then(data => {
                const permission = (data as AuthUser).attributes?.['custom:userType'];
                if (permission !== UserType.Free) {
                  handlePaidUser(data);
                } else {
                  handleFreeUser(data);
                }
                setIsLoading(false);
                sessionStorage.removeItem(StorageKey.FromLogin);
                navigate(redirectTo, { replace: true });
              })
              .catch(() => {
                notification.error({
                  message: t('Auth.notSignedIn'),
                  description: t('Common.unknownErrorDesc')
                });
                setIsLoading(false);
              })
              .finally(() => {
                setIsLoading(false);
              });
          })
          .catch(() => {
            notification.error({
              message: t('Auth.passwordNotUpdated'),
              description: t('Common.unknownErrorDesc')
            });
            setIsLoading(false);
          });
    },
    [email, handleFreeUser, handlePaidUser, navigate, redirectTo, t]
  );
  const passwordRule = usePasswordValidator();
  return (
    <Form form={form} onFinish={onFinish} initialValues={initialValues} layout={'vertical'} className={styles.form}>
      <div className={styles.heading}>
        <h3 className={styles.title}>
          <span>{t('Auth.reset.title')}</span>
        </h3>
        <span className={styles.desc}>{t('Auth.reset.desc')}</span>
      </div>
      <div className={styles.codeControl}>
        <Form.Item
          name="code"
          label={t('Auth.common.labels.code')}
          rules={[
            { required: true, message: t('Common.inputRequired', { inputName: t('Auth.code') }) },
            { pattern: new RegExp(ONLY_DIGITS_REGEX), message: t('Auth.common.errors.codePolicy') }
          ]}
        >
          <Input
            name="code"
            placeholder={t('Auth.common.placeholders.code')}
            autoComplete={'one-time-code'}
            type={'number'}
          />
        </Form.Item>
        <Button type={'default'} onClick={resendCode}>
          {t('Auth.common.buttons.resend')}
        </Button>
      </div>
      <Form.Item name="username" className={styles.hidden}>
        <Input name="username" value={email} autoComplete={'username'} type={'hidden'} />
      </Form.Item>
      <Form.Item
        name="password"
        label={<InfoLabel title={t('Auth.reset.labels.newPassword')} desc={t('Auth.passwordDescription')} />}
        rules={[
          { required: true, message: t('Auth.enterNewPassword') },
          { validator: passwordRule, message: t('Auth.passwordPolicy') }
        ]}
      >
        <Input.Password
          placeholder={t('Auth.reset.placeholders.newPassword')}
          autoComplete={'new-password'}
          type={'password'}
          iconRender={visible => <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />}
        />
      </Form.Item>
      <div className={styles.actions}>
        <Button htmlType={'submit'} type={'primary'} loading={isLoading}>
          {t('Auth.common.buttons.login')}
        </Button>
        <AuthBack />
      </div>
    </Form>
  );
};

export default ResetPasswordPage;
